import React, { useRef, useState } from "react";
import styles from "./DocUploadPopup.module.css";
import { FiX, FiUpload, FiFileText } from "react-icons/fi";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";

interface DocUploadPopupProps {
  open: boolean;
  employeeId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const DocUploadPopup: React.FC<DocUploadPopupProps> = ({
  open,
  employeeId,
  onClose,
  onSuccess,
}) => {
  const [aadhaarFile, setAadhaarFile] = useState<File | null>(null);
  const [panFile, setPanFile] = useState<File | null>(null);
  const [aadhaarUploaded, setAadhaarUploaded] = useState(false);
  const [panUploaded, setPanUploaded] = useState(false);
  const [loadingAadhaar, setLoadingAadhaar] = useState(false);
  const [loadingPan, setLoadingPan] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ aadhaar?: string; pan?: string; form?: string }>({});

  const aadhaarInputRef = useRef<HTMLInputElement | null>(null);
  const panInputRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "aadhaar" | "pan"
  ) => {
    const file = e.target.files?.[0];
    if (file && file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, [type]: "Max file size is 10MB" }));
      return;
    }
    setErrors((prev) => ({ ...prev, [type]: "" }));
    if (type === "aadhaar") setAadhaarFile(file || null);
    if (type === "pan") setPanFile(file || null);
  };

  const uploadDoc = async (docType: "aadhaar" | "pan") => {
    setErrors({});
    const file = docType === "aadhaar" ? aadhaarFile : panFile;
    if (!file) {
      setErrors((prev) => ({ ...prev, [docType]: "Please select a file to upload." }));
      return;
    }

    docType === "aadhaar" ? setLoadingAadhaar(true) : setLoadingPan(true);

    const formData = new FormData();
    formData.append("employee_id", String(employeeId));
    formData.append("doc_type", docType);
    formData.append("image", file);

    try {
      await WebService.postAPI({
        action: "/v1/door-management/employees/documents",
        body: formData,
        isFormData: true,
      });
      toast.success(`${docType === "aadhaar" ? "Aadhaar" : "PAN"} uploaded successfully!`);
      if (docType === "aadhaar") {
        setAadhaarUploaded(true);
      } else {
        setPanUploaded(true);
      }
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        [docType]: typeof err === "string" ? err : "Upload failed",
      }));
    } finally {
      docType === "aadhaar" ? setLoadingAadhaar(false) : setLoadingPan(false);
    }
  };

  const handleSaveFinish = async () => {
    setSaving(true);
    setTimeout(() => {
      toast.success("Employee successfully added!");
      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    }, 700);
  };

  const getFileName = (file: File | null) => (file ? file.name : "No file chosen");

  return (
    <>
      <div className={styles.overlay}>
        <div className={styles.panel}>
          <button className={styles.closeBtn} onClick={onClose}>
            <FiX size={26} color="#FFD400" />
          </button>
          <h2 className={styles.heading}>Upload PAN & Aadhaar Documents</h2>
          <div className={styles.uploadsCol}>
            {/* Aadhaar */}
            <div className={styles.uploadBox}>
              <label className={styles.uploadLabel}>Aadhaar Card</label>
              <div className={styles.inputRow}>
                <div
                  className={styles.uploadInput}
                  onClick={() => !aadhaarUploaded && aadhaarInputRef.current?.click()}
                  tabIndex={0}
                  style={{ background: aadhaarUploaded ? "#e6ffe6" : undefined }}
                >
                  <FiFileText size={22} />
                  <span>
                    {aadhaarUploaded
                      ? "Uploaded"
                      : getFileName(aadhaarFile)}
                  </span>
                  {!aadhaarUploaded && <FiUpload size={18} style={{ marginLeft: "auto" }} />}
                  <input
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png"
                    style={{ display: "none" }}
                    ref={aadhaarInputRef}
                    onChange={(e) => handleFileChange(e, "aadhaar")}
                  />
                </div>
                <button
                  className={styles.uploadBtn}
                  onClick={() => uploadDoc("aadhaar")}
                  disabled={loadingAadhaar || aadhaarUploaded}
                  type="button"
                >
                  {aadhaarUploaded
                    ? "Done"
                    : loadingAadhaar
                    ? "Uploading..."
                    : "Upload"}
                </button>
              </div>
              {errors.aadhaar && <div className={styles.errorMsg}>{errors.aadhaar}</div>}
            </div>

            {/* PAN */}
            <div className={styles.uploadBox}>
              <label className={styles.uploadLabel}>PAN Card</label>
              <div className={styles.inputRow}>
                <div
                  className={styles.uploadInput}
                  onClick={() => !panUploaded && panInputRef.current?.click()}
                  tabIndex={0}
                  style={{ background: panUploaded ? "#e6ffe6" : undefined }}
                >
                  <FiFileText size={22} />
                  <span>{panUploaded ? "Uploaded" : getFileName(panFile)}</span>
                  {!panUploaded && <FiUpload size={18} style={{ marginLeft: "auto" }} />}
                  <input
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png"
                    style={{ display: "none" }}
                    ref={panInputRef}
                    onChange={(e) => handleFileChange(e, "pan")}
                  />
                </div>
                <button
                  className={styles.uploadBtn}
                  onClick={() => uploadDoc("pan")}
                  disabled={loadingPan || panUploaded}
                  type="button"
                >
                  {panUploaded
                    ? "Done"
                    : loadingPan
                    ? "Uploading..."
                    : "Upload"}
                </button>
              </div>
              {errors.pan && <div className={styles.errorMsg}>{errors.pan}</div>}
            </div>
          </div>
          <div className={styles.footer}>
            <button
              className={styles.saveBtn}
              onClick={handleSaveFinish}
              disabled={saving}
              type="button"
            >
              {saving ? "Finishing..." : "Save & Finish"}
            </button>
            <button className={styles.cancelBtn} onClick={onClose} type="button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocUploadPopup;
